import { AccountType, AppMode, type IAppModeValue, LogzioTheme, PLAN_TYPE } from '@logz-pkg/enums';
import { SessionModel } from '@logz-pkg/models';
import { Observable } from '@logz-pkg/observable';
import { merge } from 'lodash';
import { authService } from '../core/auth/services/auth.service';
import { isInShareMode } from '../core/auth/tokens';
import { sessionApiService } from '../dal/session/api.service';
import { agentsApiService } from '../dal/agents/api.service';

export interface ISessionState extends Omit<SessionModel, 'user' | 'account'> {
  loggedInUser: SessionModel['user'];
  loggedInAccount: SessionModel['account'];
  appMode?: IAppModeValue;
  isSysAdmin: boolean;
}

export class SessionStateService {
  readonly session = new Observable<ISessionState>({} as ISessionState);
  appModeMap: Partial<Record<AccountType, IAppModeValue>> = {};

  fetch = async ({ fresh } = { fresh: false }) => {
    try {
      const { account, user, ...session } = (await sessionApiService.get({ fresh }))!;
      const isSysAdmin = authService.isAdmin();

      this.session.set({
        ...session,
        loggedInAccount: account,
        loggedInUser: user,
        isSysAdmin,
        appMode: this.appModeMap[account.type],
      });

      if (!isInShareMode()) {
        agentsApiService.initializeAgents().catch(() => {});
      }
    } catch {}
  };

  // Clear is an empty object so destructuring won't throw an error.
  clear = () => this.session.set({} as ISessionState);

  initAppModes = (modes: Partial<Record<AccountType, IAppModeValue>>) => {
    this.appModeMap = modes;
  };

  isLoggedIn = () => {
    const session = this.session.get();

    return Boolean(session?.loggedInUser);
  };

  // SIEM entitlement is carried on the session (userSummary.systemModes). Only entitled accounts
  // are authorized for the SIEM APIs (e.g. /siem/v2/accounts, which 403s otherwise), so consumers
  // check this before firing SIEM requests or offering the SIEM workspace.
  hasSiemAccess = (): boolean => this.session.get()?.userSummary?.systemModes?.includes(AppMode.SECURITY) ?? false;

  isTrial = async () => {
    const planType = (await sessionApiService.get())?.userSummary?.planType;

    return planType === PLAN_TYPE.TRIAL || planType === PLAN_TYPE.FREE;
  };

  setThemeWithoutSave = async (logzioTheme: LogzioTheme) => {
    const newSession = merge({}, this.session.get(), { userSettings: { logzioTheme, kibanaTheme: logzioTheme } });

    this.session.set(newSession);
  };
}

export const sessionStateService = new SessionStateService();
